import { Theme } from "@mui/material";
import { createStyles, makeStyles } from "@mui/styles";

const TABLE_HEADER_HEIGHT = "52px";
const TABLE_ROW_HEIGHT = "52px";
const TABLE_BORDER_WIDTH = "1px";
const TABLE_TOP_BORDER_RADIUS = "4px";

const tableDataCellBorder = (theme: Theme) => ({
  borderTop: "none",
  borderBottom: `${TABLE_BORDER_WIDTH} solid ${theme.palette.divider}`,
  borderLeft: "none",
  borderRight: "none",
});

const tableDataTypography = (theme: Theme) => ({
  fontFamily: theme.typography.fontFamily,
  fontWeight: 400,
  fontSize: "14px",
  lineHeight: "140%",
  letterSpacing: 0,
  textAlign: "right" as const,
  color: theme.palette.secondary.main,
});

export const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: "100%",
      backgroundColor: theme.palette.background.paper,
      border: `${TABLE_BORDER_WIDTH} solid ${theme.palette.divider}`,
      borderRadius: TABLE_TOP_BORDER_RADIUS,
      boxSizing: "border-box",
      overflow: "hidden",
      "& table": {
        width: "100%",
        borderCollapse: "collapse !important",
        borderSpacing: "0 !important",
        fontFamily: theme.typography.fontFamily,
      },
      "& thead": {
        backgroundColor: `${theme.palette.grey[400]} !important`,
        opacity: 1,
      },
      "& thead tr": {
        height: `${TABLE_HEADER_HEIGHT} !important`,
        minHeight: `${TABLE_HEADER_HEIGHT} !important`,
        maxHeight: `${TABLE_HEADER_HEIGHT} !important`,
        backgroundColor: `${theme.palette.grey[400]} !important`,
        opacity: 1,
      },
      "& th": {
        height: `${TABLE_HEADER_HEIGHT} !important`,
        minHeight: `${TABLE_HEADER_HEIGHT} !important`,
        maxHeight: `${TABLE_HEADER_HEIGHT} !important`,
        color: `${theme.palette.text.primary} !important`,
        fontSize: "14px !important",
        fontWeight: "500 !important",
        fontFamily: `${theme.typography.fontFamily} !important`,
        backgroundColor: `${theme.palette.grey[400]} !important`,
        whiteSpace: "nowrap",
        opacity: "1 !important",
        border: "none !important",
        borderBottom: `${TABLE_BORDER_WIDTH} solid ${theme.palette.divider} !important`,
        boxSizing: "border-box",
      },
      "& thead th:first-of-type": {
        borderTopLeftRadius: TABLE_TOP_BORDER_RADIUS,
      },
      "& thead th:last-of-type": {
        borderTopRightRadius: TABLE_TOP_BORDER_RADIUS,
      },
      "& tbody tr": {
        height: `${TABLE_ROW_HEIGHT} !important`,
        minHeight: `${TABLE_ROW_HEIGHT} !important`,
        maxHeight: `${TABLE_ROW_HEIGHT} !important`,
        backgroundColor: `${theme.palette.background.paper} !important`,
        opacity: 1,
      },
      "& tbody tr:hover": {
        backgroundColor: `${theme.palette.background.tableRowHover} !important`,
      },
      "& td": {
        height: `${TABLE_ROW_HEIGHT} !important`,
        minHeight: `${TABLE_ROW_HEIGHT} !important`,
        maxHeight: `${TABLE_ROW_HEIGHT} !important`,
        ...tableDataTypography(theme),
        fontFamily: `${theme.typography.fontFamily} !important`,
        fontWeight: "400 !important",
        fontSize: "14px !important",
        lineHeight: "140% !important",
        letterSpacing: "0 !important",
        textAlign: "right !important" as "right",
        color: `${theme.palette.secondary.main} !important`,
        whiteSpace: "nowrap",
        backgroundColor: `${theme.palette.background.paper} !important`,
        opacity: "1 !important",
        boxSizing: "border-box",
        ...tableDataCellBorder(theme),
        borderLeft: "none !important",
        borderRight: "none !important",
      },
      "& tbody tr:hover td": {
        backgroundColor: `${theme.palette.background.tableRowHover} !important`,
      },
      "& .MuiTableHead-root": {
        opacity: 1,
      },
      "& .MuiTableHead-root .MuiTableRow-root": {
        height: `${TABLE_HEADER_HEIGHT} !important`,
        minHeight: `${TABLE_HEADER_HEIGHT} !important`,
        maxHeight: `${TABLE_HEADER_HEIGHT} !important`,
        opacity: "1 !important",
        backgroundColor: `${theme.palette.grey[400]} !important`,
      },
      "& .MuiTableHead-root .MuiTableCell-root": {
        height: `${TABLE_HEADER_HEIGHT} !important`,
        minHeight: `${TABLE_HEADER_HEIGHT} !important`,
        maxHeight: `${TABLE_HEADER_HEIGHT} !important`,
        padding: "0 12px !important",
        textAlign: "right",
        backgroundColor: `${theme.palette.grey[400]} !important`,
        opacity: "1 !important",
        border: "none !important",
        borderBottom: `${TABLE_BORDER_WIDTH} solid ${theme.palette.divider} !important`,
        borderLeft: "none !important",
        borderRight: "none !important",
        boxSizing: "border-box",
      },
      "& .MuiTableHead-root .MuiTableCell-root:first-of-type": {
        borderTopLeftRadius: TABLE_TOP_BORDER_RADIUS,
        borderLeft: "none !important",
      },
      "& .MuiTableHead-root .MuiTableCell-root:last-of-type": {
        borderTopRightRadius: TABLE_TOP_BORDER_RADIUS,
      },
      '&[dir="rtl"] thead th:first-of-type, &[dir="rtl"] .MuiTableHead-root .MuiTableCell-root:first-of-type':
        {
          borderTopLeftRadius: 0,
          borderTopRightRadius: TABLE_TOP_BORDER_RADIUS,
        },
      '&[dir="rtl"] thead th:last-of-type, &[dir="rtl"] .MuiTableHead-root .MuiTableCell-root:last-of-type':
        {
          borderTopRightRadius: 0,
          borderTopLeftRadius: TABLE_TOP_BORDER_RADIUS,
        },
      "& .MuiTableBody-root .MuiTableRow-root": {
        height: `${TABLE_ROW_HEIGHT} !important`,
        minHeight: `${TABLE_ROW_HEIGHT} !important`,
        maxHeight: `${TABLE_ROW_HEIGHT} !important`,
        opacity: "1 !important",
        backgroundColor: `${theme.palette.background.paper} !important`,
      },
      "& .MuiTableBody-root .MuiTableCell-root": {
        height: `${TABLE_ROW_HEIGHT} !important`,
        minHeight: `${TABLE_ROW_HEIGHT} !important`,
        maxHeight: `${TABLE_ROW_HEIGHT} !important`,
        padding: "0 12px !important",
        ...tableDataTypography(theme),
        fontFamily: `${theme.typography.fontFamily} !important`,
        fontWeight: "400 !important",
        fontSize: "14px !important",
        lineHeight: "140% !important",
        letterSpacing: "0 !important",
        textAlign: "right !important" as "right",
        color: `${theme.palette.secondary.main} !important`,
        opacity: "1 !important",
        boxSizing: "border-box",
        backgroundColor: `${theme.palette.background.paper} !important`,
        ...tableDataCellBorder(theme),
        borderLeft: "none !important",
        borderRight: "none !important",
      },
      "& .MuiTableBody-root .MuiTableCell-root:first-of-type": {
        borderLeft: "none !important",
      },
    },
    statusChip: {
      minWidth: "66px",
      height: "26px",
      padding: "8px 14px",
      borderRadius: "4px",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      boxSizing: "border-box",
      opacity: 1,
      color: theme.palette.common.white,
      fontSize: "12px",
      fontWeight: 700,
      lineHeight: "18px",
      fontFamily: theme.typography.fontFamily,
      whiteSpace: "nowrap",
    },
    priorityCell: {
      display: "inline-flex",
      alignItems: "center",
      flexDirection: "row",
      direction: "rtl",
      gap: "6px",
      fontFamily: theme.typography.fontFamily,
      fontWeight: 400,
      fontSize: "14px",
      lineHeight: "21px",
      letterSpacing: 0,
      color: `${theme.palette.secondary.main} !important`,
    },
    priorityIcon: {
      fontSize: "18px !important",
    },
    priorityHIGH: {
      color: theme.palette.error.main,
    },
    priorityMEDIUM: {
      color: theme.palette.warning.main,
    },
    priorityNORMAL: {
      color: theme.palette.success.main,
    },
  })
);
